/**
 * Quality / Tests and coverage / Coverage (GET /quality/coverage): the trend of line coverage across runs (a small
 * inline line), then per service its totals and its files (lines, branches, functions, statements), sortable, the least
 * covered first. A file opens (GET /quality/coverage/file) to its source with the covered lines subtly green and the
 * uncovered ones red, line numbers in the gutter, a note when the file changed since that run, and "Write tests for this
 * file with AI" with the uncovered ranges.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Sparkle } from '@phosphor-icons/react';
import { getCoverage, getCoverageFile } from '../../services/QualityService';
import { Pill, State, SubHead } from './QualityCommon';
import DataTable from '../table/DataTable';
import { coverageFilePrompt, coverageTone, errorText, pct, runLabel, trendPoints } from './qualityModel';

const METRICS = [
  { key: 'lines', label: 'Lines' },
  { key: 'branches', label: 'Branches' },
  { key: 'functions', label: 'Functions' },
  { key: 'statements', label: 'Statements' },
];

export function CoverageTrend({ trend = [] }) {
  const values = trend.map((t) => t.lines);
  const measured = values.filter((v) => typeof v === 'number');
  if (measured.length < 2) {
    return <p className="quality-muted quality-small" data-testid="quality-cov-trend-empty">{measured.length ? 'One measured run so far: the trend shows from the second.' : 'No measured run yet.'}</p>;
  }
  const points = trendPoints(values, { width: 240, height: 48 });
  const first = measured[0];
  const last = measured[measured.length - 1];
  const label = `Line coverage over the last ${plural2(trend.length)}: from ${pct(first)} to ${pct(last)}`;
  return (
    <figure className="quality-cov-trend" data-testid="quality-cov-trend">
      <svg aria-label={label} height="48" role="img" viewBox="0 0 240 48" width="240">
        <polyline fill="none" points={points} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
      </svg>
      <figcaption className="quality-muted quality-small">{label}</figcaption>
    </figure>
  );
}

const plural2 = (n) => `${n} ${n === 1 ? 'run' : 'runs'}`;

CoverageTrend.propTypes = { trend: PropTypes.array };

function FileTable({ service, files, sort, onSort, onOpenFile, min }) {
  const columns = [
    { key: 'path', label: 'File', className: 'quality-mono quality-small', value: (f) => f.path, title: (f) => f.path, render: (f) => <button className="quality-link quality-mono" onClick={() => onOpenFile(f.path)} type="button">{f.path}</button> },
    ...METRICS.map((m) => ({
      key: m.key,
      label: m.label,
      width: 100,
      value: (f) => f[m.key],
      render: (f) => (m.key === 'lines' ? <span className={`quality-ink-${coverageTone(f.lines, min)}`}>{pct(f.lines)}</span> : pct(f[m.key])),
    })),
    { key: 'uncovered', label: 'Not covered', width: 180, className: 'quality-mono quality-small', value: (f) => f.uncovered || '–' },
  ];
  return (
    <div className="quality-table-box">
      <DataTable
        ariaLabel={`${service} files`}
        className="quality-table"
        columns={columns}
        onSort={onSort}
        rowKey="path"
        rowProps={() => ({ 'data-testid': 'quality-cov-file' })}
        rows={files}
        sort={sort}
        storageKey="flow.quality.coverageFiles.hiddenColumns"
      />
    </div>
  );
}

FileTable.propTypes = {
  files: PropTypes.array.isRequired, min: PropTypes.number, onOpenFile: PropTypes.func.isRequired, onSort: PropTypes.func.isRequired, service: PropTypes.string.isRequired, sort: PropTypes.object.isRequired,
};

export function CoverageFile({ projectId, path, runId = null, canEdit = false, onAskAI = null, onOpenCode = null, onBack }) {
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    setAnswer(null);
    getCoverageFile(projectId, { path, runId }).then((a) => live && setAnswer(a)).catch((e) => live && setError(e));
    return () => { live = false; };
  }, [projectId, path, runId]);
  const covered = new Set(answer?.lines?.covered || []);
  const uncovered = new Set(answer?.lines?.uncovered || []);
  const source = answer?.source;
  return (
    <div className="quality-sub" data-testid="quality-cov-file-view">
      <SubHead mono onBack={onBack} title={path}>
        <span className="quality-grow" />
        {answer && canEdit && onAskAI && <button className="quality-btn is-primary" onClick={() => onAskAI(coverageFilePrompt(answer))} type="button"><Sparkle aria-hidden size={13} />Write tests for this file with AI</button>}
        {onOpenCode && <button className="quality-btn" onClick={() => onOpenCode({ path })} type="button">Open in Code</button>}
      </SubHead>
      {error && <State title="Could not load this file's coverage" tone="bad">{errorText(error)}</State>}
      {!error && !answer && <div className="quality-state" role="status">Loading the file…</div>}
      {answer && (
        <>
          <p className="quality-small">
            {METRICS.map((m) => <span key={m.key}>{m.label} {pct(answer.metrics?.[m.key])} · </span>)}
            <span className="quality-muted">not covered: <span className="quality-mono">{answer.uncoveredRanges || 'none'}</span></span>
          </p>
          {answer.changedSinceRun && <p className="quality-banner is-warn" data-testid="quality-cov-changed" role="note">The file changed since this run: the highlighted lines may be off. Run the tests again for a fresh map.</p>}
          {source === null || source === undefined
            ? <p className="quality-muted">The source is not available (the file was removed, or it is too large).</p>
            : (
              <div className="quality-cov-source" data-testid="quality-cov-source">
                {String(source).split('\n').map((text, i) => {
                  const n = i + 1;
                  const state = uncovered.has(n) ? 'is-uncovered' : covered.has(n) ? 'is-covered' : '';
                  return (
                    <div className={`quality-cov-line ${state}`} data-line={n} key={n}>
                      <span aria-hidden className="quality-cov-gutter">{n}</span>
                      <code className="quality-mono">{text || ' '}</code>
                    </div>
                  );
                })}
              </div>
            )}
        </>
      )}
    </div>
  );
}

CoverageFile.propTypes = {
  canEdit: PropTypes.bool, onAskAI: PropTypes.func, onBack: PropTypes.func.isRequired, onOpenCode: PropTypes.func, path: PropTypes.string.isRequired, projectId: PropTypes.string.isRequired, runId: PropTypes.string,
};

export default function QualityCoverage({ projectId, onBack, onOpenFile, min = 70 }) {
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: 'lines', dir: 'asc' });
  const load = useCallback(async () => {
    setError(null);
    try { setAnswer(await getCoverage(projectId)); } catch (e) { setError(e); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  // One sort for every service's files.
  const onSort = setSort;
  const services = answer?.services || [];
  return (
    <div className="quality-sub" data-testid="quality-coverage-view">
      <SubHead onBack={onBack} title="Coverage" />
      {error && !answer && (
        error.code === 'not_run' || error.status === 404
          ? <State title="The tests have not run yet">Run the tests to measure the coverage.</State>
          : <State action={<button className="quality-btn" onClick={load} type="button">Try again</button>} title="Could not load the coverage" tone="bad">{errorText(error)}</State>
      )}
      {!error && !answer && <div className="quality-state" role="status">Loading the coverage…</div>}
      {answer && (
        <>
          <section aria-labelledby="quality-cov-trend-title" className="quality-card quality-pad">
            <h2 id="quality-cov-trend-title">Trend</h2>
            <CoverageTrend trend={answer.trend || []} />
            {answer.runId && <span className="quality-muted quality-small" title={answer.runId}>At the run {runLabel({ runId: answer.runId, label: answer.label })}</span>}
          </section>
          {services.map((s) => (
            <section aria-labelledby={`quality-cov-svc-${s.service}`} className="quality-card quality-pad" data-testid={`quality-cov-service-${s.service}`} key={s.service}>
              <div className="quality-section-head">
                <h2 className="quality-mono" id={`quality-cov-svc-${s.service}`}>{s.service}</h2>
                {s.measured === false
                  ? <Pill tone="info">Coverage not measured</Pill>
                  : <span className="quality-small">{METRICS.map((m) => `${m.label.toLowerCase()} ${pct(s.totals?.[m.key])}`).join(' · ')}</span>}
              </div>
              {s.measured === false && <p className="quality-muted quality-small">{s.coverageNote || 'This service\'s tests ran without a coverage report.'}</p>}
              {(s.files || []).length > 0 && <FileTable files={s.files} min={min} onOpenFile={(path) => onOpenFile(path, answer.runId || null)} onSort={onSort} service={s.service} sort={sort} />}
            </section>
          ))}
        </>
      )}
    </div>
  );
}

QualityCoverage.propTypes = {
  min: PropTypes.number,
  onBack: PropTypes.func.isRequired,
  // (path, runId) => void: the file view.
  onOpenFile: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
};
